import { call, takeLatest, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { AxiosResponse } from 'axios';
import {
  makeBackendApi,
  IInternalUserResponse,
  IOneInternalUserResponse,
  ICompaniesResponse,
  EUserType,
} from 'services/BackendApi';

import * as InternalUserActions from './actions';
import { originalUserTypeSelector } from './selectors';

import { IEnabledNotification } from 'services/BackendApi/types/Notification';
import { enqueueNotification } from '../ui';
import { IDynamicParametersResponse } from 'services/ParametersProviderApi';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';

function* successNotification(message: string) {
  yield put(enqueueNotification({ message, options: { variant: 'success' } }));
}

function* errorNotification(message: string) {
  yield put(enqueueNotification({ message, options: { variant: 'error' } }));
}

export function* createRequestSaga(action: InternalUserActions.CreateInternalUserRequestAction) {
  const backendApi = makeBackendApi();
  const notifications: IEnabledNotification[] = action.notifications.map(notification => {
    return {
      code: notification.code,
      enabled: notification.enabled ?? false,
      ccToDepartmentsUuids: notification.ccToDepartmentsUuids || [],
    };
  });

  try {
    const emailValidationResponse: AxiosResponse<IInternalUserResponse> = yield call(
      backendApi.getInternalUsersByEmail,
      action.internalUser.email ?? ''
    );
    if (emailValidationResponse.data.data.length > 0) {
      throw new Error('This email has already been registered');
    }

    const dynamicParameters: IDynamicParametersResponse = ParameterService.getParameters();
    const query = {
      'filter[company][companyId]': dynamicParameters.MAIN_COMPANY_CODE,
    };
    const companiesResponse: AxiosResponse<ICompaniesResponse> = yield call(
      backendApi.getCompanies,
      query
    );
    const mainCompany = Array.isArray(companiesResponse?.data?.data) && companiesResponse?.data?.data?.length > 0
      ? companiesResponse.data.data[0]
      : null;

    const internalUserData = {
      ...action.internalUser,
      companyUuid: mainCompany?.uuid ?? null,
    };
    const internalUserResponse: AxiosResponse<IOneInternalUserResponse> = yield call(
      backendApi.createInternalUser,
      internalUserData,
      notifications
    );
    const createdInternalUser = internalUserResponse.data.data;

    yield put(InternalUserActions.createInternalUserSuccessAction(createdInternalUser));
    yield successNotification('New user created successfully');
    yield put(InternalUserActions.resetInternalUserAction());
    yield put(push('/users'));
  } catch (e) {
    yield put(InternalUserActions.createInternalUserFailureAction(e.message));
    yield errorNotification(e.message ?? 'Failed to create a new user');
  }
}

export function* getRequestSaga(action: InternalUserActions.GetInternalUserRequestAction) {
  const backendApi = makeBackendApi();

  try {
    const internalUserResponse: AxiosResponse = yield call(backendApi.getInternalUserViaUuid, action.internalUserUuid);

    yield put(InternalUserActions.getInternalUserSuccessAction(internalUserResponse.data.data));
  } catch (e) {
    yield put(InternalUserActions.getInternalUserFailureAction(e.message));
    yield errorNotification('Failed to fetch Internal User.');
  }
}

export function* updateRequestSaga(action: InternalUserActions.UpdateInternalUserRequestAction) {
  const backendApi = makeBackendApi();
  const notifications: IEnabledNotification[] = action.notifications.map(notification => {
    return {
      code: notification.code,
      enabled: notification.enabled ?? false,
      ccToDepartmentsUuids: notification.ccToDepartmentsUuids || [],
    };
  });

  try {
    // Get original user type from Redux state to detect role changes
    const originalType: EUserType | null = yield select(originalUserTypeSelector);
    const newType = action.internalUser.type;

    const isChangingFromSR = originalType === EUserType.SR && newType !== EUserType.SR;
    const isChangingToSR = originalType !== EUserType.SR && newType === EUserType.SR;
    const isStayingSR = originalType === EUserType.SR && newType === EUserType.SR;

    // CRITICAL: If changing FROM SR to another role, clear destinations FIRST
    // The PUT endpoint only works for SR users, so we must clear before changing role
    if (isChangingFromSR) {
      yield call(backendApi.updateUserAssignedDestinations, action.internalUserUuid, []);
      yield put(InternalUserActions.updateAssignedDestinationsSuccessAction([]));
    }

    // If staying SR, update destinations before user update (order doesn't matter, but do it here)
    if (isStayingSR && action.assignedDestinations !== undefined) {
      yield call(backendApi.updateUserAssignedDestinations, action.internalUserUuid, action.assignedDestinations);
      yield put(InternalUserActions.updateAssignedDestinationsSuccessAction(action.assignedDestinations));
    }

    // Proceed with user update
    const internalUserResponse: AxiosResponse = yield call(
      backendApi.updateInternalUser,
      action.internalUserUuid,
      action.internalUser,
      notifications
    );
    const updatedInternalUser = internalUserResponse.data.data;

    // If changing TO SR from another role, update destinations AFTER user update
    // The PUT endpoint only works for SR users, so we must change role first
    if (isChangingToSR && action.assignedDestinations !== undefined && action.assignedDestinations.length > 0) {
      yield call(backendApi.updateUserAssignedDestinations, action.internalUserUuid, action.assignedDestinations);
      yield put(InternalUserActions.updateAssignedDestinationsSuccessAction(action.assignedDestinations));
    }

    yield put(InternalUserActions.updateInternalUserSuccessAction(updatedInternalUser));
    yield successNotification('Internal User updated.');
    yield put(InternalUserActions.getInternalUserRequestAction(updatedInternalUser.uuid));
  } catch (e) {
    yield put(InternalUserActions.updateInternalUserFailureAction(e.message));
    yield errorNotification('Failed to update Internal User.');
  }
}

export function* getAssignedDestinationsSaga(action: InternalUserActions.GetAssignedDestinationsRequestAction) {
  const backendApi = makeBackendApi();

  try {
    const response: AxiosResponse = yield call(backendApi.getUserAssignedDestinations, action.userUuid);
    const destinations = response.data.data.map((d: { code: string }) => d.code);
    yield put(InternalUserActions.getAssignedDestinationsSuccessAction(destinations));
  } catch (e) {
    // Don't show error notification - user might just have no destinations assigned
    yield put(InternalUserActions.getAssignedDestinationsFailureAction(e.message));
  }
}

export function* updateAssignedDestinationsSaga(action: InternalUserActions.UpdateAssignedDestinationsRequestAction) {
  const backendApi = makeBackendApi();

  try {
    yield call(backendApi.updateUserAssignedDestinations, action.userUuid, action.destinations);
    yield put(InternalUserActions.updateAssignedDestinationsSuccessAction(action.destinations));
  } catch (e) {
    yield put(InternalUserActions.updateAssignedDestinationsFailureAction(e.message));
    yield errorNotification('Failed to update assigned destinations.');
  }
}

export function* watchInternalUserSaga() {
  yield takeLatest(InternalUserActions.CREATE_REQUEST, createRequestSaga);

  yield takeLatest(InternalUserActions.GET_REQUEST, getRequestSaga);

  yield takeLatest(InternalUserActions.UPDATE_REQUEST, updateRequestSaga);

  yield takeLatest(InternalUserActions.GET_ASSIGNED_DESTINATIONS_REQUEST, getAssignedDestinationsSaga);

  yield takeLatest(InternalUserActions.UPDATE_ASSIGNED_DESTINATIONS_REQUEST, updateAssignedDestinationsSaga);
}
